/**
 * The post chain, in order:
 *
 *   1. VHS tape pass at buffer resolution (AMD-3), ahead of everything so the wobble
 *      carries into the glow instead of sliding underneath it
 *   2. bright pass with a soft knee
 *   3. dual-filter bloom, N levels down and back up, telescope-normalised
 *   4. halation off a mid pyramid level, wider and warmer than the bloom
 *   5. persistence feedback at buffer resolution
 *   6. CRT composite at output resolution, tonemapped last
 *
 * §11.1 pins the bloom: threshold 0.32, strength 0.85, radius 0.50 at default;
 * 0.45 / 0.30 in reduced mode; off disables the pass with emissive intensities
 * unchanged. §14.6 rungs 6–8 (AMD-2) switch persistence, halation and pyramid depth off
 * independently of the player's setting, and can only ever take away what the setting
 * allows — never add.
 */

import * as THREE from 'three';
import { cfg } from '../../core/config';
import { quality } from '../quality';
import { Bloom } from './Bloom';
import { CrtComposite, SCANLINE_MAX } from './CrtComposite';
import { Halation } from './Halation';
import { Persistence } from './Persistence';
import { Vhs } from './Vhs';

export type BloomMode = 'default' | 'reduced' | 'off';

/** §11.1 numbers, and the reduced-mode pair. */
export const BLOOM_DEFAULT = { threshold: 0.32, strength: 0.85, radius: 0.5 } as const;
export const BLOOM_REDUCED = { threshold: 0.32, strength: 0.45, radius: 0.3 } as const;

/**
 * AMD-6 D4 — the bright-pass floor.
 *
 * §11.1's 0.32 threshold puts every mid-tone in the frame into the bloom pyramid, so an
 * enemy body at emissive 1.2 blooms as hard as the core inside it. That is the mush: the
 * glow is the *only* layer, and nothing reads as emissive line art. Raising the floor to
 * 0.72 means only the cores bloom, which is what leaves a crisp stroke visible inside
 * the glow. The §11.1 declared numbers above are unchanged and still gate the
 * "bloom-on peak ≥ 2.0× bloom-off" acceptance, which a higher floor makes easier, not
 * harder, because the core is what that criterion measures.
 */
export const BRIGHT_PASS_FLOOR = 0.72;

/** Per-level upsample weights, widest level last. Normalised against level 0. */
const BLOOM_WEIGHTS: readonly number[] = [1.0, 0.85, 0.68, 0.52, 0.38, 0.26];
const MAX_BLOOM_LEVELS = 6;

export interface PostStackOptions {
  width: number;
  height: number;
  type: THREE.TextureDataType;
}

export class PostStack {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly vhs: Vhs;
  private readonly bloom: Bloom;
  private readonly halation: Halation;
  private readonly persistence: Persistence;
  readonly composite: CrtComposite;
  /** Bound wherever a pass is switched off, so no sampler is left dangling. */
  private readonly black: THREE.DataTexture;

  private bufferW: number;
  private bufferH: number;
  private bloomMode: BloomMode = 'default';
  /** Player settings. The §14.6 ladder ANDs with these; it never overrides them upward. */
  private wantPersistence = true;
  private wantHalation = true;

  constructor(renderer: THREE.WebGLRenderer, options: PostStackOptions) {
    this.renderer = renderer;
    this.bufferW = options.width;
    this.bufferH = options.height;

    this.vhs = new Vhs(options.width, options.height, options.type);
    this.bloom = new Bloom(options.width, options.height, options.type, MAX_BLOOM_LEVELS);
    this.halation = new Halation(options.width, options.height, options.type);
    this.persistence = new Persistence(options.width, options.height, options.type);
    this.composite = new CrtComposite();

    this.black = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
    this.black.needsUpdate = true;

    for (let i = 0; i < this.bloom.weights.length; i++) this.bloom.weights[i] = BLOOM_WEIGHTS[i] ?? 0;
    this.apply();
  }

  /** §11.1 / §13.1.3 bloom setting. `off` disables the pass; emissives are untouched. */
  setBloomMode(mode: BloomMode): void {
    this.bloomMode = mode;
    this.apply();
  }

  get bloomModeValue(): BloomMode {
    return this.bloomMode;
  }

  setPersistenceEnabled(on: boolean): void {
    this.wantPersistence = on;
    this.apply();
  }

  setHalationEnabled(on: boolean): void {
    this.wantHalation = on;
    this.apply();
  }

  /** AMD-3 slider, 0 = fully off. */
  setVhsIntensity(v: number): void {
    this.vhs.intensity = v;
  }

  /**
   * AMD-3 caps this at 0.35; AMD-55 clamps the *applied* value to 0.10 and measures the
   * period in device pixels, so the grille now survives at every viewport size. The
   * slider keeps its declared range.
   */
  setScanlineStrength(v: number): void {
    this.composite.setScanlineRequest(v);
  }

  /** AMD-55 — corner darkening. */
  setVignette(v: number): void {
    this.composite.setVignette(v);
  }

  /** AMD-55 — lomo corner softness, in output pixels. Zeroed by the governor on low tiers. */
  setEdgeBlur(px: number): void {
    this.composite.setEdgeBlur(px);
  }

  /** §11.1 exposure, settings range 0.9–1.4. */
  setExposure(v: number): void {
    this.composite.uniforms.uExposure.value = Math.max(0.9, Math.min(1.4, v));
  }

  resize(bufferW: number, bufferH: number, outputW: number, outputH: number, scale: number): void {
    this.bufferW = bufferW;
    this.bufferH = bufferH;
    this.vhs.resize(bufferW, bufferH);
    this.bloom.resize(bufferW, bufferH);
    this.halation.resize(bufferW, bufferH);
    this.persistence.resize(bufferW, bufferH);
    this.composite.setGeometry(outputW, outputH, bufferW, bufferH, scale);
    this.apply();
  }

  /** Wipes the feedback buffer. Deterministic captures want this before frame 0. */
  clearHistory(): void {
    this.persistence.reset(this.renderer);
  }

  set time(value: number) {
    this.composite.time = value;
    this.vhs.time = value;
  }

  /**
   * Runs the whole chain and leaves the result on the default framebuffer.
   * `sceneTexture` is the HDR scene buffer, already prescaled if the fit needed it.
   */
  render(sceneTexture: THREE.Texture, sceneW: number, sceneH: number): void {
    this.apply();

    let source = sceneTexture;
    if (this.vhs.intensity > 0) source = this.vhs.render(this.renderer, sceneTexture);

    const halationOn = this.wantHalation && quality.halation && this.bloomMode !== 'off';
    const persistOn = this.wantPersistence && quality.persistence;
    const bloomOn = this.bloomMode !== 'off';

    if (bloomOn) {
      this.bloom.renderDown(this.renderer, source, sceneW, sceneH);
      // Read the pyramid before the upsample writes back into it, otherwise the halation
      // source is the bloom itself and the two effects compound.
      if (halationOn) this.halation.render(this.renderer, this.bloom.mip(2));
      this.bloom.renderUp(this.renderer);
    }

    if (persistOn) this.persistence.render(this.renderer, source);

    this.composite.setTextures(
      source,
      persistOn ? this.persistence.texture : this.black,
      bloomOn ? this.bloom.texture : this.black,
      halationOn ? this.halation.texture : this.black,
    );
    this.composite.uniforms.uPersistMix.value = persistOn ? 1 : 0;
    this.composite.render(this.renderer);
  }

  /** Draw calls this stack contributes, for the §14.2 budget accounting. */
  get passCount(): number {
    let n = 1; // composite
    if (this.vhs.intensity > 0) n++;
    if (this.bloomMode !== 'off') n += this.bloom.levels * 2 - 1;
    if (this.wantHalation && quality.halation && this.bloomMode !== 'off') n += 5;
    if (this.wantPersistence && quality.persistence) n++;
    return n;
  }

  dispose(): void {
    this.vhs.dispose();
    this.bloom.dispose();
    this.halation.dispose();
    this.persistence.dispose();
    this.composite.dispose();
    this.black.dispose();
  }

  private apply(): void {
    const spec = this.bloomMode === 'reduced' ? BLOOM_REDUCED : BLOOM_DEFAULT;
    this.bloom.levels = Math.max(1, Math.min(MAX_BLOOM_LEVELS, quality.bloomLevels));
    // Knee is proportional to threshold: a fixed knee turns into a hard cut once the
    // threshold slider moves, and a hard cut pops as sprites travel.
    const threshold = Math.max(BRIGHT_PASS_FLOOR, cfg.get('render.bloom_threshold') || spec.threshold);
    this.bloom.setThreshold(threshold, 0.35 * threshold);
    this.bloom.radius = 1.0 + spec.radius;

    const strength = this.bloomMode === 'off' ? 0 : spec.strength;
    this.composite.uniforms.uBloomIntensity.value = this.bloom.normalisedGain(strength);
    this.composite.uniforms.uHalationStrength.value = this.wantHalation && quality.halation ? 0.3 : 0;

    // AMD-6 D6 — persistence at 0.5 leaves a stamped copy of a moving sprite every
    // frame, and a halo orb arrived at the reviewer as six countable rungs rather than
    // a trail. At 0.22 the feedback reads as a one-frame smear; the ribbon in
    // `trails.ts` is what carries the motion now.
    this.persistence.persistence = 0.22;
    this.persistence.trailSaturation = 1.1;
  }
}
